import { createContext, useContext, useEffect, useState } from "react";
import { getUserFromId, getUserFromNumber } from "../database/helpers/user";
import { Sync } from "../SyncService";
import Cookies from "js-cookie";
import { database } from "../database";
import { analytics } from "../config/firebase";
import { setUserId, setUserProperties } from "firebase/analytics";
import { handleApi } from "../apis";
import { POST_LOGOUT } from "../apis/configuration";
import { clearLocalStorageExcludeSomeFields } from "../utils";
import { hasUnsyncedChanges } from "@nozbe/watermelondb/sync";
import { useWalletContext } from "./WalletContext";
import * as Sentry from "@sentry/react";

export const AuthContext = createContext({
  user: null,
  business: null,
  handleLoginInDB: () => {},
  handleLogoutInDB: () => {},
  setUser: () => {},
  setBusiness: () => {},
  refreshBusiness: () => {},
  pinVerified: false,
  setPinVerified: () => {},
});

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [business, setBusiness] = useState(null);
  const [businessTick, setBusinessTick] = useState(0);
  const { setWalletData } = useWalletContext();
  // Normally false on every load (forces PIN verify). Exception: when the app
  // reloads due to business switch/delete, a sessionStorage flag bypasses it.
  const [pinVerified, setPinVerified] = useState(() => {
    const skip = sessionStorage.getItem("skip_pin_verify") === "true";
    if (skip) sessionStorage.removeItem("skip_pin_verify");
    return skip;
  });

  const refreshBusiness = () => setBusinessTick((t) => t + 1);

  useEffect(() => {
    if (
      user?.id &&
      process.env.REACT_APP_CLARITY_PROJECT_ID &&
      typeof window !== "undefined" &&
      window.clarity
    ) {
      window.clarity("set", "userId", user.id);
    }
  }, [user?.id]);

  // Sentry counts "users" (Crash Free Users) by the session's user id; without this every
  // session is anonymous and the metric stays at 0%. Id only — no phone number (PII).
  useEffect(() => {
    Sentry.setUser(user?.id ? { id: user.id } : null);
  }, [user?.id]);

  const handleLogin = (number, userId, navigate) => {
    return new Promise(async (resolve, reject) => {
      try {
        let DBuser = userId
          ? await getUserFromId(userId)
          : await getUserFromNumber(number);
        DBuser = DBuser.length ? DBuser[0] : DBuser;
        if (!DBuser.is_active)
          await DBuser.activateUser(DBuser.activation_code);
        setUserProperties(analytics, {
          user_id: number,
        });
        setUserId(analytics, number);
        setUser(DBuser);
        const activeBusiness = await DBuser.activeBusiness.fetch();
        if (activeBusiness.length) {
          setBusiness(activeBusiness[0]);
          localStorage.setItem("business_id", activeBusiness[0].id);
        } else {
          let allBusinesses = await DBuser.business.fetch();
          if (allBusinesses.length) {
            let business = await allBusinesses[0].activateBusiness();
            setBusiness(business);
          } else {
            await handleLogout(navigate);
          }
        }
        resolve({ DBuser });
      } catch (err) {
        if (user) {
          await handleLogout(navigate);
        }
        reject();
      }
    });
  };

  const handleLogout = (navigate) => {
    return new Promise(async (resolve, reject) => {
      try {
        let shouldDeleteDatabase = true;

        try {
          const syncStatus = await hasUnsyncedChanges({ database });
          if (syncStatus) {
            await Sync(user?.phone_number, navigate);
          }
        } catch {
          // unsynced data exists but sync failed — keep database intact
          shouldDeleteDatabase = false;
        }

        try {
          if (user?.phone_number) {
            await handleApi(POST_LOGOUT, null, null, navigate);
          }
        } catch {
          // logout API failed — still clear local session
        }

        if (user) await user.deactivateUser();
        clearLocalStorageExcludeSomeFields();
        sessionStorage.setItem("demo", false);
        Cookies.remove("tempUserId");

        if (shouldDeleteDatabase) {
          await user.deleteDatabase(database);
        }

        setUser(null);
        setWalletData();
        setBusiness(null);
        setPinVerified(false);
        resolve();
      } catch (err) {
        reject(err);
      }
    });
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        business,
        handleLoginInDB: handleLogin,
        handleLogoutInDB: handleLogout,
        setUser,
        setBusiness,
        refreshBusiness,
        pinVerified,
        setPinVerified,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuthContext = () => useContext(AuthContext);
